/**
 * Notifications WebSocket Service
 *
 * Historically used ActionCable (Chatwoot NotificationsChannel).
 * Production FrontDesk is EMAIL_ENGINE=native and uses HTTP polling instead.
 * ActionCable /cable reconnects were causing Cloud Run 503 storms — connect() is a no-op.
 */

import config from '@/config';
import { BackendNotification } from '@/services/frontdesk/utils/notification-type-mapper';

/**
 * Notification event data structures
 */
export interface NotificationNewEvent {
  notification: BackendNotification;
}

export interface NotificationReadEvent {
  id: string;
  readAt: Date;
}

export interface NotificationUnreadCountEvent {
  count: number;
  type?: string;
}

/**
 * Event handler types
 */
export type NotificationNewHandler = (notification: BackendNotification) => void;
export type NotificationReadHandler = (data: NotificationReadEvent) => void;
export type NotificationUnreadCountHandler = (data: NotificationUnreadCountEvent) => void;
export type ConnectionEventHandler = (connected: boolean) => void;
export type ErrorEventHandler = (error: string) => void;

/**
 * NotificationsWebSocketService
 * Realtime path disabled; keep handler API for callers that still register listeners.
 */
export class NotificationsWebSocketService {
  private serverUrl: string;
  private connected: boolean = false;

  private onNewNotificationHandler: NotificationNewHandler | null = null;
  private onNotificationReadHandler: NotificationReadHandler | null = null;
  private onUnreadCountChangeHandler: NotificationUnreadCountHandler | null = null;
  private onConnectionChangeHandler: ConnectionEventHandler | null = null;
  private onErrorHandler: ErrorEventHandler | null = null;

  constructor() {
    this.serverUrl = config.messaging_server || config.api_server;
  }

  /**
   * No-op: native FrontDesk notifications are polled over HTTP.
   */
  connect(): void {
    console.log(
      '[NotificationsWebSocket] Skipping ActionCable — using HTTP polling',
    );
  }

  disconnect(): void {
    this.connected = false;
  }

  isConnected(): boolean {
    return this.connected;
  }

  onNewNotification(handler: NotificationNewHandler): void {
    this.onNewNotificationHandler = handler;
  }

  onNotificationRead(handler: NotificationReadHandler): void {
    this.onNotificationReadHandler = handler;
  }

  onUnreadCountChange(handler: NotificationUnreadCountHandler): void {
    this.onUnreadCountChangeHandler = handler;
  }

  onConnectionChange(handler: ConnectionEventHandler): void {
    this.onConnectionChangeHandler = handler;
  }

  onError(handler: ErrorEventHandler): void {
    this.onErrorHandler = handler;
  }

  clearHandlers(): void {
    this.onNewNotificationHandler = null;
    this.onNotificationReadHandler = null;
    this.onUnreadCountChangeHandler = null;
    this.onConnectionChangeHandler = null;
    this.onErrorHandler = null;
  }
}

/**
 * Singleton instance of NotificationsWebSocketService
 */
export const notificationsWebSocketService = new NotificationsWebSocketService();
